// CTA instrumentation. One delegated click listener and one IntersectionObserver
// for the whole app, both driven by `data-cta` attributes rather than per-button
// handlers: adding a CTA to tracking is `{...ctaProps('name', 'placement')}`, with
// no import in the component and no handler to edit. Delegation also catches the
// clicks that never reach a React handler at all — plain anchor navigations, and
// taps that land on a MUI ripple child rather than the button itself.
//
// Every event goes through `trackEvent`, so it reaches both Amplitude and the GTM
// dataLayer. `ctaProps` emits `data-dd-action-name` from the same call, so
// Datadog RUM's auto-captured actions carry our name instead of deriving one from
// element text (which is empty for icon-only buttons).

import { trackEvent } from 'src/helpers/analytics';

const CTA_ATTR = 'data-cta';
const PLACEMENT_ATTR = 'data-cta-placement';
const VARIANT_ATTR = 'data-cta-variant';

// How much of a CTA has to be on screen before it counts as seen. Half the
// element keeps a control that merely clips into view at the fold from
// inflating the denominator.
const IMPRESSION_THRESHOLD = 0.5;

export interface CtaProps {
  [CTA_ATTR]: string;
  [PLACEMENT_ATTR]?: string;
  [VARIANT_ATTR]?: string;
  'data-dd-action-name': string;
}

/**
 * Spread onto any clickable element to track it:
 *
 *   <Button {...ctaProps('apply_job', 'job_detail_sticky_footer')} onClick={..} />
 *
 * `cta` is the intent and stays stable forever — it is what funnels are built on.
 * `placement` is where this instance of it lives, and is the dimension you break
 * a funnel down by to compare positions. `variant` is for A/B tests.
 */
export const ctaProps = (
  cta: string,
  placement?: string,
  variant?: string,
): CtaProps => ({
  [CTA_ATTR]: cta,
  [PLACEMENT_ATTR]: placement,
  [VARIANT_ATTR]: variant,
  // The `cta_` prefix is what makes our CTAs separable from Datadog's
  // auto-derived action names, which come from element text and frequently
  // contain a "·" of their own (job cards render "Company · Location · Salary").
  // One stable `@action.target.name:cta_*` filter then finds every tagged CTA,
  // including ones added later, instead of a hardcoded name list that silently
  // goes stale. Underscore, NOT a colon: Datadog's query parser reads a colon
  // in a value as a facet separator, so `@action.target.name:cta:*` silently
  // matches nothing at all.
  'data-dd-action-name': placement ? `cta_${cta} · ${placement}` : `cta_${cta}`,
});

// The react-router pattern for the current view ('/job-search/:jobAlias'), not
// the resolved pathname. Reported from inside the router by useRoutePattern;
// this module is loaded before React mounts and cannot call hooks itself.
// Without the pattern, `page` would carry one distinct value per job alias and
// every Amplitude breakdown would be unusable.
let currentRoutePattern = window.location.pathname;

// The resolved pathname behind that pattern. Tracked separately because the
// impression dedup has to reset per *navigation*, and two different pages very
// often share one pattern — job A and job B are both
// '/job-search/:jobAlias'. Keying the reset on the pattern meant browsing ten
// jobs in a row logged one impression and ten clicks, which inflates every CTR
// it is supposed to measure.
let currentPathname = window.location.pathname;

// Which (cta, placement) pairs have already logged an impression on this view.
// Reset per navigation, so the metric reads "on what share of the pageviews
// where this placement was seen did someone click it" — deliberately once per
// view rather than once per element, so a list of 20 job cards emits one
// impression, not 20. That keeps event volume proportional to navigation
// instead of to scroll depth.
const seenThisView = new Set<string>();

const readCta = (el: Element) => ({
  cta: el.getAttribute(CTA_ATTR) || undefined,
  placement: el.getAttribute(PLACEMENT_ATTR) || undefined,
  variant: el.getAttribute(VARIANT_ATTR) || undefined,
});

const impressionKey = (cta?: string, placement?: string) =>
  `${cta}|${placement}`;

let impressionObserver: IntersectionObserver | null = null;

const observeWithin = (root: ParentNode) => {
  if (!impressionObserver) return;
  root
    .querySelectorAll(`[${CTA_ATTR}]`)
    .forEach((el) => impressionObserver?.observe(el));
};

/**
 * Called by useRoutePattern on every navigation. `pattern` is what events are
 * tagged with; `pathname` is what decides whether this is a new pageview.
 *
 * Clearing the dedup set and restarting the observer is also what bounds
 * memory: an IntersectionObserver holds a strong reference to every target, so
 * CTAs that scrolled out of a previous view and were unmounted would otherwise
 * be retained for the life of the session.
 */
export const setCurrentView = (pattern: string, pathname: string): void => {
  currentRoutePattern = pattern;
  if (pathname === currentPathname) return;
  currentPathname = pathname;
  seenThisView.clear();
  if (impressionObserver) {
    impressionObserver.disconnect();
    observeWithin(document);
  }
};

export const getRoutePattern = (): string => currentRoutePattern;

/**
 * Installs the listeners. Call once, before React mounts. Safe to call twice.
 */
let isInitialised = false;

export const initCtaTracking = (): void => {
  if (isInitialised || typeof window === 'undefined') return;
  isInitialised = true;

  // Capture phase, so a handler that calls stopPropagation (common on cards
  // with nested actions) cannot swallow the event before we see it.
  document.addEventListener(
    'click',
    (event) => {
      try {
        const target = event.target as HTMLElement | null;
        const el = target?.closest?.(`[${CTA_ATTR}]`);
        if (!el) return;
        const { cta, placement, variant } = readCta(el);
        if (!cta) return;

        // Recorded so a control the UI refused is distinguishable from one it
        // accepted. Note this only reaches us for `aria-disabled` and for
        // non-native controls styled as disabled — the browser suppresses
        // click entirely on a native <button disabled>, so those never arrive
        // here at all.
        const isDisabled =
          el.hasAttribute('disabled') ||
          el.getAttribute('aria-disabled') === 'true';

        trackEvent('cta_click', {
          cta,
          placement,
          variant,
          page: currentRoutePattern,
          disabled: isDisabled || undefined,
        });
      } catch {
        // analytics best-effort — never break a click
      }
    },
    { capture: true },
  );

  if (typeof IntersectionObserver === 'undefined') return;

  impressionObserver = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        // Stop watching this element either way: it has either been counted, or
        // its (cta, placement) pair was already counted on this view.
        obs.unobserve(entry.target);

        const { cta, placement, variant } = readCta(entry.target);
        if (!cta) return;
        const key = impressionKey(cta, placement);
        if (seenThisView.has(key)) return;
        seenThisView.add(key);

        trackEvent('cta_viewed', {
          cta,
          placement,
          variant,
          page: currentRoutePattern,
        });
      });
    },
    { threshold: IMPRESSION_THRESHOLD },
  );

  observeWithin(document);

  // CTAs mount long after this runs — lazy routes, dialogs, infinite lists.
  const mo = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;
        if (node.hasAttribute(CTA_ATTR)) impressionObserver?.observe(node);
        observeWithin(node);
      });
    });
  });
  mo.observe(document.body, { childList: true, subtree: true });
};
